import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { deletePasskey, listPasskeys, type PasskeyDescription } from "@/auth/cognitoIdp";
import { hasAdminScope } from "@/auth/jwt";
import { setLoginHint } from "@/auth/loginHint";
import { passkeysSupported, registerThisDevice } from "@/auth/passkeys";
import { markStepUpPending } from "@/auth/stepUp";
import { ErrorBox, MutationError, Spinner } from "@/components/Spinner";
import { Button, EmptyState, Page, PageHeader } from "@/components/ui";
import { formatDate, fromEpochSeconds } from "@/lib/dates";

function PasskeyRow({
  passkey,
  confirming,
  onAskRemoval,
  onConfirmRemoval,
  onCancel,
  busy,
}: {
  passkey: PasskeyDescription;
  confirming: boolean;
  onAskRemoval: () => void;
  onConfirmRemoval: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div>
        <div className="text-sm font-medium text-ink">{passkey.FriendlyCredentialName}</div>
        <div className="text-xs text-ink-muted">
          Added {formatDate(fromEpochSeconds(passkey.CreatedAt), { zone: "local" })}
        </div>
      </div>
      {confirming ? (
        <div className="flex items-center gap-2">
          <Button variant="danger" loading={busy} onClick={onConfirmRemoval}>
            Confirm removal
          </Button>
          <Button variant="secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button variant="secondary" onClick={onAskRemoval}>
          Remove
        </Button>
      )}
    </li>
  );
}

export function SecurityPage() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? "";
  // Enrôler, lister et retirer un passkey exigent un jeton portant le scope
  // admin. Une session ordinaire ne l'a pas : il faut d'abord se réauthentifier.
  // Une session Touch ID l'a d'emblée (jeton obtenu par l'API directement).
  const unlocked = hasAdminScope(token);
  const queryClient = useQueryClient();
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  const passkeys = useQuery({
    queryKey: ["passkeys"],
    queryFn: () => listPasskeys(token),
    enabled: unlocked,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["passkeys"] });
  const register = useMutation({
    mutationFn: () => registerThisDevice(token),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (credentialId: string) => deletePasskey(token, credentialId),
    onSuccess: () => {
      setPendingRemoval(null);
      return refresh();
    },
  });

  return (
    <Page width="form">
      <PageHeader title="Security" />
      <div className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-ink">Touch ID</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Sign in with your fingerprint instead of a password and an authentication code. Your
            password and authenticator app keep working as a fallback.
          </p>
        </div>

        {!unlocked ? (
          <div className="space-y-3">
            <p className="text-sm text-ink">
              To add or remove Touch ID, confirm your identity. You will be signed out, then asked
              for your password and authentication code again.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                // Le Hosted UI classique ignore `prompt=login` : on le déconnecte
                // d'abord, et la page « Signed out » relance la connexion avec le
                // scope des passkeys. Sans cookie de session, le mot de passe et
                // le TOTP sont redemandés. Voir auth/stepUp.ts.
                markStepUpPending();
                setLoginHint("password");
                void auth.signoutRedirect();
              }}
            >
              Confirm my identity
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {passkeys.isLoading && <Spinner label="Loading…" />}
            {passkeys.error && <ErrorBox error={passkeys.error} />}
            {passkeys.data && passkeys.data.length === 0 && (
              <EmptyState size="inline" title="No Touch ID is registered yet." />
            )}
            {passkeys.data && passkeys.data.length > 0 && (
              <ul className="divide-y divide-line">
                {passkeys.data.map((passkey) => (
                  <PasskeyRow
                    key={passkey.CredentialId}
                    passkey={passkey}
                    confirming={pendingRemoval === passkey.CredentialId}
                    busy={remove.isPending}
                    onAskRemoval={() => setPendingRemoval(passkey.CredentialId)}
                    onConfirmRemoval={() => {
                      register.reset();
                      remove.mutate(passkey.CredentialId);
                    }}
                    onCancel={() => {
                      // Cancelling drops the failure of the removal it was about, like a dialog's Cancel does.
                      remove.reset();
                      setPendingRemoval(null);
                    }}
                  />
                ))}
              </ul>
            )}
            <MutationError mutation={remove} title="Could not remove Touch ID" />
            <MutationError mutation={register} title="Could not register Touch ID" />
            {!passkeysSupported() && (
              <p className="text-sm text-ink-muted">This browser does not support Touch ID.</p>
            )}
            <Button
              variant="primary"
              loading={register.isPending} disabled={!passkeysSupported()}
              onClick={() => {
                remove.reset();
                register.mutate();
              }}
            >
              {register.isPending ? "Waiting for Touch ID…" : "Register Touch ID on this device"}
            </Button>
          </div>
        )}
      </div>
    </Page>
  );
}
